// /home/labdoo/zitech-nexus/frontend/src/pages/admin/ContactMessageDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contactAPI } from '../../services/api';
import './ContactMessageDetail.css';

// SVG Icons
const Icons = {
  Back: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  User: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Email: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Calendar: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Message: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Send: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  Check: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="cmd-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  StatusDot: () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
};

interface ContactMessage {
  id: number;
  name: string;
  email: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

const ContactMessageDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<ContactMessage | null>(null);
  const [replyEmail, setReplyEmail] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replySent, setReplySent] = useState(false);

  useEffect(() => {
    fetchMessage();
  }, [id]);

  const fetchMessage = async () => {
    try {
      const response = await contactAPI.getMessage(parseInt(id!));
      setMessage(response.data);
      setReplyEmail(response.data.email);
      // Mark as read when viewing
      if (!response.data.is_read) {
        await contactAPI.markAsRead(parseInt(id!));
      }
    } catch (error) {
      console.error('Error fetching message:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyMessage.trim()) {
      alert('Please enter a reply message');
      return;
    }

    setSendingReply(true);
    try {
      await contactAPI.sendReply(parseInt(id!), {
        reply_message: replyMessage
      });
      setReplySent(true);
      setTimeout(() => {
        navigate('/dashboard/admin/contact');
      }, 3000);
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const getStatusColor = (isRead: boolean) => {
    return isRead ? '#10b981' : '#ef4444';
  };

  const getStatusBackground = (isRead: boolean) => {
    return isRead ? '#d1fae5' : '#fee2e2';
  };

  const getStatusLabel = (isRead: boolean) => {
    return isRead ? 'Read' : 'Unread';
  };

  if (loading) {
    return (
      <div className="cmd-loading-container">
        <div className="cmd-spinner">
          <Icons.Spinner />
        </div>
        <p className="cmd-loading-text">Loading message...</p>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="cmd-error-container">
        <div className="cmd-error-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2 className="cmd-error-title">Message Not Found</h2>
        <p className="cmd-error-text">The message you're looking for doesn't exist or has been removed.</p>
        <button className="cmd-error-btn" onClick={() => navigate('/dashboard/admin/contact')}>
          <Icons.Back />
          Back to Messages
        </button>
      </div>
    );
  }

  return (
    <div className="cmd-container">
      {/* Header */}
      <div className="cmd-header">
        <div className="cmd-header-left">
          <button className="cmd-back-btn" onClick={() => navigate('/dashboard/admin/contact')}>
            <Icons.Back />
            Back to Messages
          </button>
          <h1 className="cmd-title">Message from {message.name}</h1>
        </div>
        <div className="cmd-header-status">
          <span 
            className="cmd-status-badge-large"
            style={{
              background: getStatusBackground(message.is_read),
              color: getStatusColor(message.is_read)
            }}
          >
            <Icons.StatusDot />
            {getStatusLabel(message.is_read)}
          </span>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="cmd-stats-bar">
        <div className="cmd-stat-item">
          <span className="cmd-stat-label">From</span>
          <span className="cmd-stat-value">
            <Icons.User />
            {message.name}
          </span>
        </div>
        <div className="cmd-stat-divider"></div>
        <div className="cmd-stat-item">
          <span className="cmd-stat-label">Email</span>
          <span className="cmd-stat-value cmd-stat-email">
            <a href={`mailto:${message.email}`}>
              <Icons.Email />
              {message.email}
            </a>
          </span>
        </div>
        <div className="cmd-stat-divider"></div>
        <div className="cmd-stat-item">
          <span className="cmd-stat-label">Received</span>
          <span className="cmd-stat-value">
            <Icons.Calendar />
            {new Date(message.created_at).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Detail Grid */}
      <div className="cmd-detail-grid">
        {/* Message Content */}
        <div className="cmd-card cmd-message-card">
          <h3 className="cmd-card-title">
            <Icons.Message />
            Message
          </h3>
          <div className="cmd-message-text">
            <p>{message.message}</p>
          </div>
        </div>

        {/* Reply Section */}
        <div className="cmd-card cmd-reply-card">
          {!replySent ? (
            <>
              <h3 className="cmd-card-title">
                <Icons.Send />
                Reply to {message.name}
              </h3>
              <form onSubmit={handleSendReply} className="cmd-reply-form">
                <div className="cmd-form-group">
                  <label className="cmd-form-label">To:</label>
                  <input
                    type="email"
                    value={replyEmail}
                    onChange={(e) => setReplyEmail(e.target.value)}
                    className="cmd-form-input"
                    required
                  />
                </div>
                <div className="cmd-form-group">
                  <label className="cmd-form-label">Reply Message:</label>
                  <textarea
                    rows={6}
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    className="cmd-form-textarea"
                    placeholder="Type your reply here..."
                    required
                  />
                </div>
                <div className="cmd-form-actions">
                  <button 
                    type="button" 
                    className="cmd-btn-secondary"
                    onClick={() => navigate('/dashboard/admin/contact')}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="cmd-btn-primary"
                    disabled={sendingReply}
                  >
                    {sendingReply ? (
                      <>
                        <span className="cmd-spinner-small"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Icons.Send />
                        Send Reply
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="cmd-reply-success">
              <div className="cmd-success-icon">
                <Icons.Check />
              </div>
              <h3 className="cmd-success-title">Reply Sent Successfully!</h3>
              <p className="cmd-success-text">
                Your reply has been sent to <strong>{message.email}</strong>
              </p>
              <button 
                className="cmd-success-btn"
                onClick={() => navigate('/dashboard/admin/contact')}
              >
                <Icons.Back />
                Back to Messages
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactMessageDetail;